import React, { useState } from 'react';

import { CalculatedWebJoint, WebSegment } from '@paragon/contract-library';
import { RaycastOrder, RenderOrder } from '@paragon/three-shared';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { getTransformationPropsForSegment } from '../../library/utilities/updateMatrixFromSegment';
import { handleStandardSceneItemRightClick, useClickItem } from '../../store/actions/sceneInteractionActions';
import { clearHoverItemIfActive, ensureHoverItemIsActive } from '../../store/actions/viewportActions';
import { WEB_COLOR, WEB_HOVER_COLOR, WEB_SELECTED_COLOR } from './WebJointSegmentVisual';

interface Props {
  readonly webSegment: WebSegment;
  readonly baseJoint: CalculatedWebJoint;
  readonly endJoint: CalculatedWebJoint;
}

export function WebSegmentVisual({ webSegment, baseJoint, endJoint }: Props) {
  const dispatch = useAppDispatch();

  const [hovered, setHovered] = useState(false);

  const selectedItems = useAppSelector(getSelectedItemIdentifiers);
  const selected = selectedItems.some(({ type, guid }) => type === 'webSegment' && guid === webSegment.guid);

  const interactionHandle = { type: 'webSegment', guid: webSegment.guid, origin: 'fiber' } as const;
  const onClick = useClickItem({ type: 'webSegment', item: webSegment });

  return (
    <group>
      <object3D>
        <mesh
          renderOrder={RenderOrder.WebSegmentVisual}
          {...getTransformationPropsForSegment({ ...baseJoint.location, z: 0 }, { ...endJoint.location, z: 0 })}
        >
          <cylinderGeometry args={[0.25, 0.25, 1, 8, 1, false]} />
          <meshBasicMaterial
            color={selected ? WEB_SELECTED_COLOR : hovered ? WEB_HOVER_COLOR : WEB_COLOR}
            transparent
            depthTest={false}
            depthWrite={false}
            opacity={1}
          />
        </mesh>

        <mesh
          renderOrder={RenderOrder.WebSegmentVisual}
          {...getTransformationPropsForSegment({ ...baseJoint.location, z: 0 }, { ...endJoint.location, z: 0 })}
          onClick={onClick}
          onPointerOver={(event) => {
            event.stopPropagation();

            setHovered(true);
            dispatch(ensureHoverItemIsActive(interactionHandle));
          }}
          onPointerOut={(event) => {
            event.stopPropagation();

            setHovered(false);
            dispatch(clearHoverItemIfActive(interactionHandle));
          }}
          onContextMenu={(event) => {
            event.stopPropagation();
            event.nativeEvent.preventDefault();

            dispatch(handleStandardSceneItemRightClick(event, interactionHandle));
          }}
          userData-raycastOrder={RaycastOrder.ProfileWebSegmentVisual}
        >
          <cylinderGeometry args={[1.5, 1.5, 1, 8, 1, false]} />
          <meshBasicMaterial
            color={selected ? WEB_SELECTED_COLOR : hovered ? WEB_HOVER_COLOR : WEB_COLOR}
            transparent
            depthTest={false}
            depthWrite={false}
            opacity={0}
          />
        </mesh>
      </object3D>
    </group>
  );
}
